export enum OSType {
  Windows = 1,
  Linux = 2,
}

export enum AgentCapability {
  CpuMonitoring = 1,
  MemoryMonitoring = 2,
  DiskMonitoring = 3,
  IisMonitoring = 4,
  WindowsTaskMonitoring = 5,
  WindowsServiceMonitoring = 6,
  LinuxCronMonitoring = 7,
}

export enum SqlServerType {
  MSSQL = 1,
  POSTGRESQL = 2,
  MYSQL = 3,
}

export interface Agent {
  id: string;
  serverId: string;
  serverName: string;
  tenantId: string;
  tenantName: string;
  agentVersion: string;
  osType: OSType;
  osTypeName: string;
  capabilities: string; // JSON string
  lastHeartbeat?: string;
  lastBankServiceRunCheckAt?: string;
  configVersion: number;
  heartbeatIntervalSeconds: number;
  configPullIntervalSeconds: number;
  healthCheckIntervalSeconds: number;
  metricsCollectionIntervalSeconds: number;
  heartbeatTimeoutMultiplier: number;
  cpuWarningThreshold?: number;
  cpuCriticalThreshold?: number;
  ramWarningThreshold?: number;
  ramCriticalThreshold?: number;
  diskThresholds: string; // JSON string: [{"drive": "C:", "warning": 70, "critical": 90}, ...]
  isOnline: boolean;
  heartbeatStatus: number; // 0: Unknown, 1: Online, 2: Offline
  heartbeatStatusName: string;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  // eStatement bağlantı ayarları
  eStatementConnectionString?: string;
  eStatementSqlServerType?: SqlServerType;
  eStatementSonIslemZamaniIntervalSeconds?: number;
  eStatementSirketlerCekmeSikligiSeconds?: number;
  bankServiceRunIntervalSeconds?: number;
  eStatementDataProtectionEnabled?: boolean;
  eStatementDataProtectionKeyPath?: string;
  runServiceAdminPasswd?: string;
}

export interface CreateAgentRequest {
  serverId: string;
  tenantId: string;
  agentVersion: string;
  osType: OSType;
  capabilities: string; // JSON string
  configVersion: number;
  heartbeatIntervalSeconds: number;
  configPullIntervalSeconds: number;
  healthCheckIntervalSeconds: number;
  metricsCollectionIntervalSeconds: number;
  heartbeatTimeoutMultiplier: number;
  cpuWarningThreshold?: number;
  cpuCriticalThreshold?: number;
  ramWarningThreshold?: number;
  ramCriticalThreshold?: number;
  diskThresholds?: string; // JSON string: [{"drive": "C:", "warning": 70, "critical": 90}, ...]
  // eStatement bağlantı ayarları
  eStatementConnectionString?: string;
  eStatementSqlServerType?: SqlServerType;
  eStatementSonIslemZamaniIntervalSeconds?: number;
  eStatementSirketlerCekmeSikligiSeconds?: number;
  bankServiceRunIntervalSeconds?: number;
  eStatementDataProtectionEnabled?: boolean;
  eStatementDataProtectionKeyPath?: string;
  runServiceAdminPasswd?: string;
}

export interface UpdateAgentRequest {
  id: string;
  // serverId ve tenantId değiştirilemez (agent bir server'a bağlı)
  agentVersion: string;
  osType: OSType;
  capabilities: string; // JSON string
  configVersion: number;
  heartbeatIntervalSeconds: number;
  configPullIntervalSeconds: number;
  healthCheckIntervalSeconds: number;
  metricsCollectionIntervalSeconds: number;
  heartbeatTimeoutMultiplier: number;
  cpuWarningThreshold?: number;
  cpuCriticalThreshold?: number;
  ramWarningThreshold?: number;
  ramCriticalThreshold?: number;
  diskThresholds?: string; // JSON string: [{"drive": "C:", "warning": 70, "critical": 90}, ...]
  isActive: boolean;
  // eStatement bağlantı ayarları
  eStatementConnectionString?: string;
  eStatementSqlServerType?: SqlServerType;
  eStatementSonIslemZamaniIntervalSeconds?: number;
  eStatementSirketlerCekmeSikligiSeconds?: number;
  bankServiceRunIntervalSeconds?: number;
  eStatementDataProtectionEnabled?: boolean;
  eStatementDataProtectionKeyPath?: string;
  runServiceAdminPasswd?: string;
}

// Capability helper functions
export const capabilityHelper = {
  toJson: (capabilities: AgentCapability[]): string => {
    const obj: Record<string, boolean> = {};
    capabilities.forEach((cap) => {
      obj[AgentCapability[cap]] = true;
    });
    return JSON.stringify(obj);
  },
  fromJson: (json: string): AgentCapability[] => {
    try {
      const obj = JSON.parse(json);
      return Object.keys(obj)
        .filter((key) => obj[key] === true)
        .map((key) => AgentCapability[key as keyof typeof AgentCapability])
        .filter((cap) => cap !== undefined) as AgentCapability[];
    } catch {
      return [];
    }
  },
  /** Özellik (capability) açık mı? Metrik/UI gösteriminde kullanılır. */
  hasCapability: (capabilitiesJson: string | undefined, capability: AgentCapability): boolean => {
    if (!capabilitiesJson || capabilitiesJson === '{}') return false;
    try {
      const obj = JSON.parse(capabilitiesJson) as Record<string, boolean>;
      const key = AgentCapability[capability];
      return obj[key] === true;
    } catch {
      return false;
    }
  },
  getSupportedCapabilities: (osType: OSType): AgentCapability[] => {
    const allCapabilities = [
      AgentCapability.CpuMonitoring,
      AgentCapability.MemoryMonitoring,
      AgentCapability.DiskMonitoring,
      AgentCapability.IisMonitoring,
      AgentCapability.WindowsTaskMonitoring,
      AgentCapability.WindowsServiceMonitoring,
      AgentCapability.LinuxCronMonitoring,
    ];
    if (osType === OSType.Windows) {
      return allCapabilities.filter(
        (cap) => cap !== AgentCapability.LinuxCronMonitoring
      );
    } else {
      return allCapabilities.filter(
        (cap) =>
          cap !== AgentCapability.IisMonitoring &&
          cap !== AgentCapability.WindowsTaskMonitoring &&
          cap !== AgentCapability.WindowsServiceMonitoring
      );
    }
  },
  getCapabilitiesForOS: (osType: OSType): AgentCapability[] => {
    return capabilityHelper.getSupportedCapabilities(osType);
  },
  capabilitiesToJson: (capabilities: AgentCapability[]): string => {
    return capabilityHelper.toJson(capabilities);
  },
  jsonToCapabilities: (json: string): AgentCapability[] => {
    return capabilityHelper.fromJson(json);
  },
  getApplicationTypeForCapability: (capability: AgentCapability): number | null => {
    switch (capability) {
      case AgentCapability.IisMonitoring:
        return 1; // ApplicationType.IisApp
      case AgentCapability.WindowsTaskMonitoring:
        return 2; // ApplicationType.WindowsTask
      case AgentCapability.WindowsServiceMonitoring:
        return 3; // ApplicationType.WindowsService
      case AgentCapability.LinuxCronMonitoring:
        return 4; // ApplicationType.LinuxCron
      default:
        return null;
    }
  },
  getSupportedApplicationTypes: (capabilities: AgentCapability[]): number[] => {
    const types: number[] = [];
    capabilities.forEach((cap) => {
      const type = capabilityHelper.getApplicationTypeForCapability(cap);
      if (type !== null) {
        types.push(type);
      }
    });
    return types;
  },
  getCapabilityLabel: (capability: AgentCapability): string => {
    const labels: Record<AgentCapability, string> = {
      [AgentCapability.CpuMonitoring]: 'CPU İzleme',
      [AgentCapability.MemoryMonitoring]: 'Memory İzleme',
      [AgentCapability.DiskMonitoring]: 'Disk İzleme',
      [AgentCapability.IisMonitoring]: 'IIS İzleme',
      [AgentCapability.WindowsTaskMonitoring]: 'Windows Task İzleme',
      [AgentCapability.WindowsServiceMonitoring]: 'Windows Service İzleme',
      [AgentCapability.LinuxCronMonitoring]: 'Linux Cron İzleme',
    };
    return labels[capability] || capability.toString();
  },
};


// CapabilityApplicationDefinition - AgentForm'da kullanılan interface
export interface CapabilityApplicationDefinition {
  id?: string; // ApplicationDefinition id (edit için)
  name: string;
  // IIS App fields
  siteName?: string;
  appPoolName?: string;
  expectedSiteState?: string;
  expectedAppPoolState?: string;
  healthCheckUrl?: string;
  httpMethod?: string;
  httpStatusCodeExpected?: number;
  responseTimeThresholdMs?: number | string;
  timeoutMs?: number | string;
  // Windows Task fields
  taskName?: string;
  lastRunResultExpected?: string;
  shouldBeRunning?: boolean;
  maxDelayMinutes?: number; // HealthRule'dan
  // Windows Service fields
  serviceName?: string;
  expectedStatus?: string;
  autoRestartEnabled?: boolean;
  restartAttemptCount?: number;
  // Linux Cron fields
  jobName?: string;
  schedule?: string;
  lastExecutionStatusExpected?: string;
  // Common fields
  expectedRunIntervalMinutes?: number;
}
